package com.eladfinish.bible.contest.ui.scaffold

import androidx.compose.material3.SnackbarDuration
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.SnackbarResult
import com.eladfinish.bible.contest.data.common.stringResolver.StringResolver
import com.eladfinish.bible.contest.extensions.orEmpty
import dev.icerock.moko.resources.StringResource
import dev.icerock.moko.resources.desc.StringDesc
import dev.icerock.moko.resources.desc.desc
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

typealias ShowSnackbar = (
    content: StringDesc,
    actionLabel: StringDesc?,
    duration: SnackbarDuration,
    onActionPerformed: () -> Unit,
) -> Unit

interface SnackbarActions {
    fun showToast(message: String, duration: SnackbarDuration = SnackbarDuration.Short)
    fun showToast(message: StringDesc, duration: SnackbarDuration = SnackbarDuration.Short)
    fun showToast(message: StringResource, duration: SnackbarDuration = SnackbarDuration.Short)

    fun showSnackbar(message: String)
    fun showSnackbar(
        message: StringDesc,
        actionLabel: StringDesc? = null,
        duration: SnackbarDuration = SnackbarDuration.Short,
        onActionPerformed: () -> Unit = {},
    )

    fun showSnackbar(message: StringResource)

    /** Shows an indefinite snackbar with a spinner; dismiss it via the host's dismiss action. */
    fun showLoadingSnackbar(message: String)
}

fun createSnackbarActions(
    hostState: SnackbarHostState,
    scope: CoroutineScope,
    stringResolver: StringResolver,
): SnackbarActions =
    SnackbarActionsImpl(hostState, scope, stringResolver)

private class SnackbarActionsImpl(
    private val hostState: SnackbarHostState,
    private val scope: CoroutineScope,
    private val stringResolver: StringResolver,
) : SnackbarActions {

    // The texts are resolved inside the coroutine, with the suspending resolver: on the web the
    // synchronous one returns "" the first time it sees a string, which showed blank messages.
    private fun showToastInternal(message: StringDesc, duration: SnackbarDuration) {
        scope.launch {
            hostState.showSnackbar(
                visuals = AppSnackbarVisuals(
                    message = stringResolver.resolveStringSuspending(message),
                    kind = MessageKind.TOAST,
                    duration = duration,
                )
            )
        }
    }

    private fun showSnackbarInternal(
        message: StringDesc,
        actionLabel: StringDesc? = null,
        duration: SnackbarDuration = SnackbarDuration.Short,
        onActionPerformed: () -> Unit = {},
    ) {
        scope.launch {
            hostState.showSnackbar(
                visuals = AppSnackbarVisuals(
                    message = stringResolver.resolveStringSuspending(message),
                    kind = MessageKind.SNACKBAR,
                    withDismissAction = true,
                    actionLabel = actionLabel?.let { stringResolver.resolveStringSuspending(it) },
                    duration = duration,
                )
            ).let {
                when (it) {
                    SnackbarResult.Dismissed -> Unit
                    SnackbarResult.ActionPerformed -> onActionPerformed()
                }
            }
        }
    }

    override fun showToast(message: String, duration: SnackbarDuration) =
        showToastInternal(message = message.desc(), duration = duration)

    override fun showToast(message: StringDesc, duration: SnackbarDuration) =
        showToastInternal(message = message, duration = duration)

    override fun showToast(message: StringResource, duration: SnackbarDuration) =
        showToastInternal(message = message.desc(), duration = duration)

    override fun showSnackbar(message: String) =
        showSnackbarInternal(message.desc())

    override fun showSnackbar(
        message: StringDesc,
        actionLabel: StringDesc?,
        duration: SnackbarDuration,
        onActionPerformed: () -> Unit,
    ) {
        showSnackbarInternal(
            message = message,
            actionLabel = actionLabel.orEmpty(),
            duration = duration,
            onActionPerformed = onActionPerformed,
        )
    }

    override fun showSnackbar(message: StringResource) =
        showSnackbarInternal(message = message.desc())

    override fun showLoadingSnackbar(message: String) {
        scope.launch {
            hostState.showSnackbar(
                visuals = AppSnackbarVisuals(
                    message = message,
                    kind = MessageKind.LOADING,
                    duration = SnackbarDuration.Indefinite,
                )
            )
        }
    }
}
